import faker from 'faker'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createChatSocket } from 'src/Helpers/createSocket'
import { experimentalStyled as styled } from '@material-ui/core/styles'
import {
  Divider,
  IconButton,
  Stack,
  CircularProgress,
  Box,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import SendIcon from '@material-ui/icons/Send'
import EmojiPickerChatSignWise from 'src/components/EmojiPickerChatSignWise'
import { DOMAIN, REST_API_IO } from 'src/constants/DefaultValues'
import { useSnackbar } from 'notistack'
import {
  ref as imageref,
  uploadBytesResumable,
  getDownloadURL,
} from '@firebase/storage'
import { storage } from 'src/firebase/firebase'
import ReactQuill from 'react-quill'
import { supabase } from '../../../../supabase/supabaseClient'
import { useTheme } from '@mui/material'
import { WA } from '../whatsappTheme'
import { wrapMessageWithReply } from '../chatReply'
import Typography from '@material-ui/core/Typography'

const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024
const MAX_VIDEO_ATTACHMENT_BYTES = 100 * 1024 * 1024

const ATTACHMENT_ACCEPT =
  'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.zip,.rar,.7z'

/** Insert a visible line break in Quill (Shift+Enter). */
const insertQuillLineBreak = (quill, range) => {
  if (!quill || !range) return
  const index = range.index
  if (range.length > 0) {
    quill.deleteText(index, range.length, 'user')
  }
  quill.insertText(index, '\n', 'user')
  quill.setSelection(index + 1, 0, 'user')
}

/** Override Quill defaults: Enter sends, Shift+Enter inserts a new line. */
const buildChatKeyboardBindings = (sendMessage, extraBindings = {}) => ({
  enter: {
    key: 13,
    shiftKey: false,
    handler: function () {
      sendMessage()
      return false
    },
  },
  linebreak: {
    key: 13,
    shiftKey: true,
    handler: function (range) {
      insertQuillLineBreak(this.quill, range)
      return false
    },
  },
  ...extraBindings,
})

const fileNameFromUrl = (url = '') => {
  try {
    const path = String(url).split('?')[0]
    const name = decodeURIComponent(path.split('/').pop() || '')
    return name || 'file'
  } catch {
    return 'file'
  }
}

const guessMimeFromName = (name = '') => {
  const lower = String(name).toLowerCase()
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower)) return 'image/*'
  if (/\.(mp4|webm|ogg|mov|m4v|avi|mkv)$/.test(lower)) return 'video/*'
  if (/\.(mp3|wav|m4a|aac|flac)$/.test(lower)) return 'audio/*'
  if (/\.pdf$/.test(lower)) return 'application/pdf'
  if (/\.(xlsx?|xls|csv)$/.test(lower)) return 'application/vnd.ms-excel'
  if (/\.(docx?|doc)$/.test(lower)) return 'application/msword'
  return 'application/octet-stream'
}

const isVideoAttachment = (item) => {
  if (!item) return false
  if (typeof item === 'string') {
    return /\.(mp4|webm|ogg|mov|m4v|avi|mkv)(\?|$)/i.test(item)
  }
  return (
    String(item.mime || '').startsWith('video/') ||
    /\.(mp4|webm|ogg|mov|m4v|avi|mkv)(\?|$)/i.test(item.url || item.name || '')
  )
}

const maxBytesForFile = (file) => {
  const mime = String(file?.type || '').toLowerCase()
  const name = String(file?.name || '')
  if (mime.startsWith('video/') || /\.(mp4|webm|ogg|mov|m4v|avi|mkv)$/i.test(name)) {
    return MAX_VIDEO_ATTACHMENT_BYTES
  }
  return MAX_ATTACHMENT_BYTES
}

const normalizeAttachments = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!item) return null
        if (typeof item === 'string') {
          return { url: item, name: fileNameFromUrl(item), mime: guessMimeFromName(item) }
        }
        return {
          id: item.id || item.url || '',
          url: item.url || '',
          name: item.name || fileNameFromUrl(item.url) || 'file',
          mime: item.mime || guessMimeFromName(item.name || item.url),
          uploading: Boolean(item.uploading),
        }
      })
      .filter((item) => item?.url)
  }
  if (typeof value === 'string' && value) {
    return [{ url: value, name: fileNameFromUrl(value), mime: guessMimeFromName(value) }]
  }
  return []
}

const isImageAttachment = (item) => {
  if (!item) return false
  if (typeof item === 'string') {
    return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(item)
  }
  return (
    String(item.mime || '').startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(item.url || item.name || '')
  )
}

const getComposerAttachmentKind = (item) => {
  if (!item) return 'file'
  if (isImageAttachment(item)) return 'image'
  const mime = String(item.mime || '').toLowerCase()
  const name = String(item.name || item.url || '')
  if (mime.startsWith('video/') || /\.(mp4|webm|ogg|mov|m4v|avi|mkv)(\?|$)/i.test(name)) return 'video'
  if (mime.startsWith('audio/') || /\.(mp3|wav|m4a|aac|flac)(\?|$)/i.test(name)) return 'audio'
  if (mime === 'application/pdf' || /\.pdf(\?|$)/i.test(name)) return 'pdf'
  return 'file'
}

const RootStyle = styled('div')(({ theme }) => ({
  minHeight: 75,
  maxHeight: 75,
  minWidth: 400,
  maxWidth: 400,
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: theme.spacing(2),
  width: '100%',
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)'
    : 'linear-gradient(135deg, rgba(18, 18, 18, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%)',
  backdropFilter: 'blur(10px)',
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'light' 
    ? '0 -2px 8px rgba(0, 0, 0, 0.08)' 
    : '0 -2px 8px rgba(0, 0, 0, 0.2)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'light'
      ? 'linear-gradient(135deg, rgba(2, 125, 194, 0.02) 0%, rgba(2, 125, 194, 0.05) 100%)'
      : 'linear-gradient(135deg, rgba(2, 125, 194, 0.1) 0%, rgba(2, 125, 194, 0.05) 100%)',
    pointerEvents: 'none',
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
  '& .ql-toolbar': {
    border: 'none !important',
    padding: '4px !important',
    maxHeight: '20px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .ql-container': {
    border: 'none !important',
    fontSize: '14px',
    position: 'relative !important',
  },
  // '& .ql-editor': {
  //   maxHeight: '60px',
  //   overflowY: 'auto',
  //   whiteSpace: 'pre-wrap',
  //   wordBreak: 'break-word',
  //   padding: '4px !important',
  //   scrollbarWidth: 'thin',
  //   scrollbarColor: ' #1a1a1a',
  //   // display: 'flex',
  //   // flexWrap: 'wrap',
  //   // alignItems: 'center',
  // },
  '& .ql-editor': {
    minHeight: '38px',
    maxHeight: '100px', // ⬅️ grows up to ~5 lines
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    padding: '6px 8px',
  },
  '& .ql-toolbar button': {
    width: '15px',
    height: '15px',
    padding: '2px',
    color: '#d3d3d3!important',
  },
  '& .ql-toolbar button svg': {
    width: '16px',
    height: '16px',
  },
  '& .ql-toolbar button:hover': {
    color: '#1ccaff !important',
  },
  '& .ql-toolbar .ql-active': {
    color: '#1ccaff !important',
  },
  '& .ql-editor::before': {
    color: '#d3d3d3 !important',
    fontStyle: 'normal !important',
    fontSize: '14px !important',
  },
  '& .ql-tooltip': {
    zIndex: 1050,
    position: 'absolute !important',
    left: '50% !important',
    transform: 'translateX(-50%) !important',
    top: '-40px !important',
    backgroundColor: '#00191e !important',
    color: 'white !important',
    padding: '6px 8px !important',
    borderRadius: '6px !important',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2) !important',
    border: '1px solid #555 !important',
    fontSize: '12px !important',
  },

  '& .ql-tooltip input': {
    backgroundColor: '#00191e !important',
    color: '#f5f5f5 !important',
    padding: '4px !important',
    borderRadius: '4px !important',
    border: '1px solid #555 !important',
    outline: 'none !important',
  },

  '& .ql-tooltip a.ql-action': {
    color: '#1ccaff !important',
  },

  '& .ql-tooltip a.ql-remove': {
    color: '#ff4d4d !important',
  },
}))

ChatMessageInputSignWise.propTypes = {
  disabled: PropTypes.bool,
  conversationId: PropTypes.string,
  onSend: PropTypes.func,
}

export default function ChatMessageInputSignWise({
  selectedChatLogId,
  disabled,
  handleRefresh,
  refreshChat,
  setRenderChat,
  renderChat,
  message,
  setMessage,
  image,
  setImage,
  fetchMessages,
  conversationId,
  onSend,
  chat,
  setChat,
  isTyping,
  setIsTyping,
  typingUser,
  setTypingUser,
  setOnChatScreen,
  participants,
  whatsapp = false,
  placeholder = 'Type a message',
  welcomeLayout = false,
  replyTo = null,
  onClearReply = null,
  ...other
}) {
  const fileInputRef = useRef(null)
  const [progress, setProgress] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  let user = JSON.parse(localStorage.getItem('user'))
  let updatedUserId = `user_${user.user_id}`
  const typingTimeout = useRef(null)
  const socketRef = useRef(null)
  const quillRef = useRef(null)
  const theme = useTheme()
  const fetchMessagesRef = useRef(fetchMessages)
  fetchMessagesRef.current = fetchMessages
  const attachments = normalizeAttachments(image)
  const hasAttachments = attachments.length > 0
  const isUploadingAttachments = progress || attachments.some((att) => att.uploading)

  const clearAttachments = () => {
    attachments.forEach((att) => {
      if (String(att.url || '').startsWith('blob:')) {
        try {
          URL.revokeObjectURL(att.url)
        } catch (e) {
          // ignore
        }
      }
    })
    if (typeof setImage === 'function') setImage([])
  }

  const removeAttachmentAt = (index) => {
    if (typeof setImage !== 'function') return
    const target = attachments[index]
    if (target && String(target.url || '').startsWith('blob:')) {
      try {
        URL.revokeObjectURL(target.url)
      } catch (e) {
        // ignore
      }
    }
    setImage(attachments.filter((_, i) => i !== index))
  }

  useEffect(() => {
    const editorRoot = quillRef.current?.editor?.root

    // const handlePaste = async (e) => {
    //   if (!e.clipboardData) return;

    //   const items = e.clipboardData.items;
    //   for (let i = 0; i < items.length; i++) {
    //     const item = items[i];

    //     // 🔐 Intercept image paste
    //     if (item.type.indexOf("image") !== -1) {
    //       e.preventDefault(); // 🔥 Prevent Quill from inserting base64

    //       const file = item.getAsFile();
    //       if (file) {
    //         const filename = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
    //         const filePath = `chat-signwise/attachments/${filename}`;

    //         try {
    //           setProgress(true);

    //           const { error: uploadError } = await supabase.storage
    //             .from('signwise-attachments')
    //             .upload(filePath, file);
    //           if (uploadError) throw uploadError;

    //           const { data: publicUrlData, error: urlError } = supabase.storage
    //             .from('signwise-attachments')
    //             .getPublicUrl(filePath);

    //           if (urlError || !publicUrlData?.publicUrl) {
    //             throw new Error('Failed to get public URL');
    //           }

    //           const imageUrl = publicUrlData.publicUrl;

    //           // Open modal with uploaded image
    //           setImagePreview(imageUrl);
    //           setImage(imageUrl);
    //           setOpenImagePreview(true);
    //           setProgress(false);
    //         } catch (error) {
    //           console.error("Image paste failed:", error.message);
    //           enqueueSnackbar("Image paste failed", { variant: "error" });
    //           setProgress(false);
    //         }
    //       }
    //     }
    //   }
    // };

    const handlePaste = async (e) => {
      if (!e.clipboardData) return

      const items = e.clipboardData.items
      for (let i = 0; i < items.length; i++) {
        const item = items[i]

        if (item.type.indexOf('image') !== -1) {
          e.preventDefault()

          const file = item.getAsFile()
          if (file) {
            const filename = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`
            const filePath = `chat-signwise/attachments/${filename}`

            try {
              setProgress(true)

              const { error: uploadError } = await supabase.storage
                .from('signwise-attachments')
                .upload(filePath, file)
              if (uploadError) throw uploadError

              const { data: publicUrlData, error: urlError } = supabase.storage
                .from('signwise-attachments')
                .getPublicUrl(filePath)

              if (urlError || !publicUrlData?.publicUrl) {
                throw new Error('Failed to get public URL')
              }

              const imageUrl = publicUrlData.publicUrl

              // ✅ Insert image at cursor position
              const quill = quillRef.current.getEditor()
              const range = quill.getSelection(true)
              const insertAt = range ? range.index : quill.getLength()

              quill.insertEmbed(insertAt, 'image', imageUrl) // paste image
              quill.insertText(insertAt + 1, '\n') // add newline
              quill.insertText(insertAt + 2, '\n') // optional: more space
              quill.setSelection(insertAt + 3) // place cursor below

              setProgress(false)
            } catch (error) {
              console.error('Image paste failed:', error.message)
              enqueueSnackbar('Image paste failed', { variant: 'error' })
              setProgress(false)
            }
          }
        }
      }
    }

    editorRoot?.addEventListener('paste', handlePaste)
    return () => {
      editorRoot?.removeEventListener('paste', handlePaste)
    }
  }, [])

  useEffect(() => {
    let notificationSound = new Audio('/sounds/whatsapp_tone.mp3')
    if (user.user_id === 667) {
      notificationSound = new Audio('/sounds/barking_dog.mp3')
    }
    socketRef.current = createChatSocket()
    const socket = socketRef.current

    const onMessage = (msg) => {
      if (msg?.domain === DOMAIN) {
        const currentUserId = user?.user_id
        let receivedConversationId = String(msg.conversationId)
        let senderConversationId = String(msg.senderId)
        const isGroupChat =
          Array.isArray(msg.participants) && msg.participants.length > 0
        const isOwnMessage = senderConversationId === String(currentUserId)
        const conversationIdStr = String(conversationId || '')
        if (conversationIdStr.startsWith('user_')) {
          if (!receivedConversationId.startsWith('user_'))
            receivedConversationId = `user_${receivedConversationId}`
          if (!senderConversationId.startsWith('user_'))
            senderConversationId = `user_${senderConversationId}`
        }
        let isSameConversation = false
        let isRecipientInGroup = true
        if (isGroupChat) {
          const participantIds = msg.participants.map((p) => p.user_id)
          isRecipientInGroup = participantIds.includes(currentUserId)
          isSameConversation =
            receivedConversationId === conversationIdStr ||
            receivedConversationId === conversationIdStr.replace(/^chat_/, '') ||
            `chat_${receivedConversationId}` === conversationIdStr
        } else {
          isSameConversation =
            (receivedConversationId === updatedUserId &&
              senderConversationId === conversationIdStr) ||
            (receivedConversationId === conversationIdStr &&
              senderConversationId === updatedUserId)
        }
        if (isSameConversation && isRecipientInGroup) {
          if (
            selectedChatLogId != null &&
            msg.selectedChatLogId != null &&
            msg.selectedChatLogId === selectedChatLogId
          ) {
            setChat((prev) => [...prev, msg])
          }

          if (!isOwnMessage) {
            try {
              notificationSound.play()
            } catch (e) {
              console.error('Failed to play sound:', e)
            }
            setTimeout(() => {
              if (typeof fetchMessagesRef.current === 'function') {
                fetchMessagesRef.current({ silent: true })
              }
            }, 400)
          }
        }
      }
    }

    const withUserPrefix = (value) => {
      const raw = String(value || '')
      if (!raw || raw.startsWith('user_') || raw.startsWith('chat_')) return raw
      return `user_${raw}`
    }

    const onTyping = (data) => {
      if (data?.domain !== DOMAIN) return
      const senderId = String(data?.senderId ?? '')
      const currentUserId = String(user?.user_id ?? '')
      if (!senderId || senderId === currentUserId) return

      const openId = String(conversationId || '')
      let receivedConversationId = String(data?.conversationId || '')
      const participants = Array.isArray(data?.participants) ? data.participants : []
      const isGroupChat =
        participants.length > 0 ||
        openId.startsWith('chat_') ||
        receivedConversationId.startsWith('chat_')

      if (isGroupChat) {
        const participantIds = participants.map((p) => String(p.user_id ?? p.id))
        const isRecipient =
          participantIds.length === 0 || participantIds.includes(currentUserId)
        const sameGroup =
          receivedConversationId === openId ||
          receivedConversationId === openId.replace(/^chat_/, '') ||
          `chat_${receivedConversationId}` === openId
        if (isRecipient && sameGroup) {
          if (typeof setTypingUser === 'function') {
            setTypingUser(data.user || { user_id: Number(senderId) })
          }
          if (typeof setIsTyping === 'function') setIsTyping(true)
        }
        return
      }

      const me = withUserPrefix(currentUserId)
      const open = openId.startsWith('user_') || openId.startsWith('chat_')
        ? openId
        : withUserPrefix(openId)
      const received = withUserPrefix(receivedConversationId)
      const senderKey = withUserPrefix(senderId)
      const matches =
        received === me ||
        received === open ||
        senderKey === open ||
        (received === updatedUserId && senderKey === open)
      if (matches) {
        if (typeof setTypingUser === 'function') {
          setTypingUser(data.user || { user_id: Number(senderId) })
        }
        if (typeof setIsTyping === 'function') setIsTyping(true)
      }
    }

    const onStopTyping = (data) => {
      if (data?.domain !== DOMAIN) return
      const senderId = String(data?.senderId ?? '')
      const currentUserId = String(user?.user_id ?? '')
      if (!senderId || senderId === currentUserId) return

      const openId = String(conversationId || '')
      let receivedConversationId = String(data?.conversationId || '')
      const participants = Array.isArray(data?.participants) ? data.participants : []
      const isGroupChat =
        participants.length > 0 ||
        openId.startsWith('chat_') ||
        receivedConversationId.startsWith('chat_')

      if (isGroupChat) {
        const participantIds = participants.map((p) => String(p.user_id ?? p.id))
        const isRecipient =
          participantIds.length === 0 || participantIds.includes(currentUserId)
        const sameGroup =
          receivedConversationId === openId ||
          receivedConversationId === openId.replace(/^chat_/, '') ||
          `chat_${receivedConversationId}` === openId
        if (isRecipient && sameGroup) {
          if (typeof setIsTyping === 'function') setIsTyping(false)
          if (typeof setTypingUser === 'function') setTypingUser('')
        }
        return
      }

      const me = withUserPrefix(currentUserId)
      const open = openId.startsWith('user_') || openId.startsWith('chat_')
        ? openId
        : withUserPrefix(openId)
      const received = withUserPrefix(receivedConversationId)
      const senderKey = withUserPrefix(senderId)
      if (received === me || received === open || senderKey === open) {
        if (typeof setIsTyping === 'function') setIsTyping(false)
        if (typeof setTypingUser === 'function') setTypingUser('')
      }
    }

    socket.on('message', onMessage)
    socket.on('typing', onTyping)
    socket.on('stop typing', onStopTyping)

    return () => {
      socket.off('message', onMessage)
      socket.off('typing', onTyping)
      socket.off('stop typing', onStopTyping)
    }
  }, [conversationId])

  const handleTyping = () => {
    if (!socketRef.current || typeof socketRef.current.emit !== 'function') return
    const payload = {
      user,
      conversationId,
      senderId: user?.user_id,
      participants: participants || null,
      selectedChatLogId,
      domain: DOMAIN,
    }
    socketRef.current.emit('typing', payload)
    if (typingTimeout.current) clearTimeout(typingTimeout.current)
    typingTimeout.current = setTimeout(() => {
      socketRef.current?.emit('stop typing', {
        conversationId,
        senderId: user?.user_id,
        participants: participants || null,
        domain: DOMAIN,
      })
    }, 2000)
  }

  const handleAttach = () => {
    fileInputRef.current.click()
  }
  // const quillModules = {
  //   toolbar: {
  //     container: [['bold', 'italic', 'underline', 'strike', 'clean', 'link', 'image']],
  //     handlers: {
  //       image: handleAttach,
  //     }
  //   },
  //   clipboard: {
  //     matchVisual: false,
  //   }
  // };

  const handleSendRef = useRef(() => {})

  const quillModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike', 'clean', 'link', 'image'],
      ],
      handlers: {
        image: handleAttach,
      },
    },
    clipboard: {
      matchVisual: false,
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    keyboard: {
      bindings: buildChatKeyboardBindings(() => handleSendRef.current?.(), {
        undo: {
          key: 'z',
          shortKey: true,
          handler: function () {
            this.quill.history.undo()
          },
        },
        redo: {
          key: 'z',
          shortKey: true,
          shiftKey: true,
          handler: function () {
            this.quill.history.redo()
          },
        },
      }),
    },
  }

  const whatsappQuillModules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline', 'link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
      clipboard: {
        matchVisual: false,
      },
      history: {
        delay: 500,
        maxStack: 100,
        userOnly: true,
      },
      keyboard: {
        bindings: buildChatKeyboardBindings(() => handleSendRef.current?.()),
      },
    }),
    [],
  )

  // Keep Quill link tooltip open so the URL field can be typed into
  useEffect(() => {
    if (!whatsapp) return undefined
    const holdFocus = (event) => {
      event.stopPropagation()
      // Keep editor selection, but allow the URL input to take focus for typing
      if (event.target?.tagName !== 'INPUT' && event.target?.tagName !== 'A') {
        event.preventDefault()
      }
    }
    let boundTip = null
    const bindTooltip = () => {
      const quill = quillRef.current?.getEditor?.()
      const tip = quill?.theme?.tooltip?.root
      if (!tip || tip === boundTip) return
      if (boundTip) {
        boundTip.removeEventListener('mousedown', holdFocus)
        boundTip.removeEventListener('pointerdown', holdFocus)
      }
      tip.addEventListener('mousedown', holdFocus)
      tip.addEventListener('pointerdown', holdFocus)
      boundTip = tip
    }
    bindTooltip()
    const intervalId = window.setInterval(bindTooltip, 400)
    return () => {
      window.clearInterval(intervalId)
      if (boundTip) {
        boundTip.removeEventListener('mousedown', holdFocus)
        boundTip.removeEventListener('pointerdown', holdFocus)
      }
    }
  }, [whatsapp])

  // Admin full chat uses non-whatsapp Quill; keyboard module bindings are unreliable there.
  useEffect(() => {
    if (whatsapp) return undefined

    let editorRoot = null
    let onKeyDown = null

    const attach = () => {
      const quill = quillRef.current?.getEditor?.()
      const root = quill?.root
      if (!root || root === editorRoot) return

      if (editorRoot && onKeyDown) {
        editorRoot.removeEventListener('keydown', onKeyDown, true)
      }

      editorRoot = root
      onKeyDown = (event) => {
        if (event.key !== 'Enter' && event.keyCode !== 13) return

        if (event.shiftKey) {
          event.preventDefault()
          event.stopPropagation()
          const range = quill.getSelection(true)
          if (range) insertQuillLineBreak(quill, range)
          return
        }

        event.preventDefault()
        event.stopPropagation()
        handleSendRef.current?.()
      }

      editorRoot.addEventListener('keydown', onKeyDown, true)
    }

    attach()
    const timerId = window.setInterval(attach, 300)
    return () => {
      window.clearInterval(timerId)
      if (editorRoot && onKeyDown) {
        editorRoot.removeEventListener('keydown', onKeyDown, true)
      }
    }
  }, [whatsapp])

  const insertEmojiIntoQuill = (emojiChar) => {
    const quill = quillRef.current?.getEditor?.()
    if (!quill) {
      setMessage((prev) => `${prev || ''}${emojiChar}`)
      return
    }
    const range = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1) }
    quill.insertText(range.index, emojiChar, 'user')
    quill.setSelection(range.index + String(emojiChar).length, 0, 'user')
  }

  const whatsappQuillSx = {
    flexGrow: 1,
    minWidth: 0,
    position: 'relative',
    bgcolor: welcomeLayout ? 'transparent' : '#F4F6F8',
    borderRadius: welcomeLayout ? 0 : '16px',
    border: welcomeLayout ? 'none' : `1px solid ${WA.border}`,
    overflow: 'visible',
    '& .quill': {
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'transparent',
      border: 'none',
      borderRadius: welcomeLayout ? 0 : '16px',
      overflow: 'visible',
    },
    '& .ql-toolbar.ql-snow': {
      border: 'none',
      borderBottom: `1px solid ${WA.border}`,
      padding: '4px 72px 4px 8px',
      minHeight: 34,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '2px 0',
      overflow: 'visible',
    },
    '& .ql-toolbar .ql-picker-options': {
      zIndex: 20,
    },
    '& .ql-toolbar .ql-formats': {
      marginRight: 6,
      display: 'inline-flex',
      alignItems: 'center',
    },
    '& .ql-toolbar button': {
      width: 26,
      height: 26,
      padding: 2,
    },
    '& .ql-toolbar button svg': {
      width: 15,
      height: 15,
    },
    '& .ql-toolbar .ql-picker': {
      fontSize: 12,
      height: 26,
      color: WA.meta,
    },
    '& .ql-toolbar .ql-picker-label': {
      paddingLeft: 4,
      paddingRight: 2,
      border: 'none',
    },
    '& .ql-container.ql-snow': {
      border: 'none',
      fontSize: 15,
      overflow: 'visible !important',
      position: 'relative',
    },
    '& .ql-editor': {
      minHeight: 36,
      maxHeight: 96,
      overflowY: 'auto',
      padding: '8px 12px',
      lineHeight: 1.4,
      color: WA.text,
    },
    '& .ql-editor.ql-blank::before': {
      color: '#919EAB',
      fontStyle: 'normal',
      fontWeight: welcomeLayout && !hasAttachments ? 600 : 400,
      left: 12,
      right: 12,
    },
    '& .ql-tooltip': {
      zIndex: 1300,
      left: '8px !important',
      right: '8px !important',
      top: 'auto !important',
      bottom: '100% !important',
      marginBottom: '6px',
      transform: 'none !important',
      position: 'absolute !important',
      width: 'auto !important',
      maxWidth: 'calc(100% - 16px)',
      backgroundColor: '#fff !important',
      color: `${WA.text} !important`,
      border: `1px solid ${WA.border} !important`,
      borderRadius: '8px !important',
      boxShadow: '0 6px 18px rgba(0,0,0,0.14) !important',
      padding: '8px 10px !important',
      fontSize: '13px !important',
      whiteSpace: 'normal',
    },
    '& .ql-tooltip.ql-flip': {
      top: 'auto !important',
      bottom: '100% !important',
      transform: 'none !important',
    },
    '& .ql-tooltip::before, & .ql-tooltip::after': {
      display: 'none !important',
    },
    '& .ql-tooltip input[type="text"]': {
      display: 'inline-block',
      width: 'min(220px, 55vw) !important',
      maxWidth: '100%',
      backgroundColor: '#F4F6F8 !important',
      color: `${WA.text} !important`,
      border: `1px solid ${WA.border} !important`,
      borderRadius: '6px !important',
      padding: '4px 8px !important',
      outline: 'none !important',
      marginRight: '6px',
    },
    '& .ql-tooltip a.ql-preview': {
      display: 'inline-block',
      maxWidth: 180,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'middle',
      color: `${WA.send} !important`,
      marginRight: 8,
    },
    '& .ql-tooltip a.ql-action, & .ql-tooltip a.ql-remove': {
      color: `${WA.send} !important`,
      marginLeft: 6,
    },
  }

  const renderWhatsappQuill = (fieldPlaceholder) => (
    <Box sx={whatsappQuillSx}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={message}
        readOnly={disabled}
        modules={whatsappQuillModules}
        placeholder={fieldPlaceholder}
        onChange={(val) => {
          setMessage(val)
          const cleaned = String(val || '').replace(/<(.|\n)*?>/g, '').trim()
          if (cleaned.length > 0) handleTyping()
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          right: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          zIndex: 2,
        }}
      >
        <EmojiPickerChatSignWise
          disabled={disabled}
          onEmojiSelect={insertEmojiIntoQuill}
          iconButtonSx={{
            width: 26,
            height: 26,
            p: 0.25,
            color: WA.meta,
          }}
        />
        <IconButton
          type="button"
          size="small"
          disabled={disabled || progress}
          onClick={handleAttach}
          aria-label="Attach file"
          sx={{
            width: 26,
            height: 26,
            p: 0.25,
            color: WA.meta,
            borderRadius: 0.75,
          }}
        >
          {progress ? (
            <CircularProgress size={14} sx={{ color: WA.send }} />
          ) : (
            <AttachFileIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  )

  const sendMessage = (messageBody = message) => {
    const newMessage = {
      conversationId,
      message: messageBody,
      sender: user,
      senderId: user.user_id,
      participants,
      selectedChatLogId,
      domain: DOMAIN,
    }
    socketRef.current.emit('message', newMessage)
    setMessage('')
    clearTimeout(typingTimeout.current)
    socketRef.current.emit('stop typing', {
      conversationId,
      senderId: user.user_id,
      participants,
      domain: DOMAIN,
    })
  }

  const handleSend = () => {
    let cleanedMessage = message.replace(/<p><br><\/p>/g, '').trim()
    if (!cleanedMessage && !hasAttachments) return
    if (attachments.some((att) => att.uploading)) {
      enqueueSnackbar('Please wait for uploads to finish', { variant: 'info' })
      return
    }

    const hasCaption = Boolean(cleanedMessage)
    const captionMessage = hasCaption ? wrapMessageWithReply(cleanedMessage, replyTo) : null

    if (!hasAttachments) {
      sendMessage(captionMessage)
      if (onSend) {
        onSend({
          conversationId,
          messageId: faker.datatype.uuid(),
          message: captionMessage || '',
          contentType: 'text',
          attachment: null,
          createdAt: new Date(),
          senderId: user?.user_id,
        })
      }
    } else {
      if (captionMessage) {
        sendMessage(captionMessage)
      }

      if (onSend) {
        attachments.forEach((att, index) => {
          const isImg = isImageAttachment(att)
          const isVideo = isVideoAttachment(att)
          onSend({
            conversationId,
            messageId: faker.datatype.uuid(),
            message: index === 0 ? captionMessage || '' : '',
            contentType: isImg ? 'image' : isVideo ? 'video' : 'file',
            attachment: att.url,
            createdAt: new Date(),
            senderId: user?.user_id,
          })
        })
      }
    }

    setMessage('')
    clearAttachments()
    if (typeof onClearReply === 'function') onClearReply()
  }
  handleSendRef.current = handleSend
  const handleSendStickers = (
    messageContent = message,
    attachmentUrl = null,
  ) => {
    if (!messageContent.trim() && !attachmentUrl) return

    sendMessage()

    if (onSend) {
      onSend({
        conversationId,
        messageId: faker.datatype.uuid(),
        message: messageContent,
        contentType: attachmentUrl ? 'gif' : hasAttachments ? 'image' : 'text',
        attachment: attachmentUrl || attachments[0]?.url || '',
        createdAt: new Date(),
        senderId: user?.user_id,
      })
    }

    setMessage('')
    clearAttachments()
  }

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length) return

    const tooLarge = files.find((file) => file.size > maxBytesForFile(file))
    if (tooLarge) {
      enqueueSnackbar('Please upload files smaller than 100 MB', { variant: 'error' })
      return
    }

    const pending = files.map((file, index) => ({
      id: `pending-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
      url: URL.createObjectURL(file),
      name: file.name,
      mime: file.type || guessMimeFromName(file.name),
      uploading: true,
      file,
    }))

    if (typeof setImage === 'function') {
      setImage([...attachments, ...pending.map(({ file, ...rest }) => rest)])
    }

    try {
      setProgress(true)
      for (const item of pending) {
        const filename = `${Date.now()}_${item.name.replace(/\s+/g, '_')}`
        const filePath = `chat-signwise/attachments/${filename}`
        const { error: uploadError } = await supabase.storage
          .from('signwise-attachments')
          .upload(filePath, item.file)
        if (uploadError) throw uploadError

        const { data: publicUrlData, error: urlError } = supabase.storage
          .from('signwise-attachments')
          .getPublicUrl(filePath)

        if (urlError || !publicUrlData?.publicUrl) {
          throw new Error('Failed to get public URL')
        }

        const publicUrl = publicUrlData.publicUrl
        if (typeof setImage === 'function') {
          setImage((prev) =>
            normalizeAttachments(prev).map((att) => {
              if (att.id !== item.id) return att
              if (String(att.url || '').startsWith('blob:')) {
                try {
                  URL.revokeObjectURL(att.url)
                } catch (e) {
                  // ignore
                }
              }
              return {
                id: item.id,
                url: publicUrl,
                name: item.name,
                mime: item.mime,
                uploading: false,
              }
            }),
          )
        }
      }
      setProgress(false)
    } catch (error) {
      console.error('Upload failed:', error.message)
      setProgress(false)
      if (typeof setImage === 'function') {
        setImage((prev) =>
          normalizeAttachments(prev).filter((att) => {
            const failed = pending.some((item) => item.id === att.id)
            if (failed && String(att.url || '').startsWith('blob:')) {
              try {
                URL.revokeObjectURL(att.url)
              } catch (e) {
                // ignore
              }
            }
            return !failed
          }),
        )
      }
      enqueueSnackbar('Failed to upload file. Please try again.', {
        variant: 'error',
      })
    }
  }

  const isMessageValid = (msg) => {
    const hasText = msg.replace(/<(.|\n)*?>/g, '').trim().length > 0
    const hasImage = /<img\s+[^>]*src="[^"]+"[^>]*>/i.test(msg)
    return hasText || hasImage
  }

  if (whatsapp) {
    const captionPlaceholder = hasAttachments ? 'Add a caption...' : placeholder
    const attachmentPreviewBar = hasAttachments ? (
      <Box
        sx={{
          px: 1.5,
          pt: 1.25,
          bgcolor: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {attachments.map((att, index) => (
          <Box
            key={att.id || `${att.url}-${index}`}
            sx={{
              position: 'relative',
              width: 96,
              height: 96,
              flexShrink: 0,
              borderRadius: 1.5,
              overflow: 'hidden',
              border: `1px solid ${WA.border}`,
              bgcolor: '#EEF1F4',
            }}
          >
            {(() => {
              const kind = getComposerAttachmentKind(att)
              if (kind === 'image') {
                return (
                  <Box
                    component="img"
                    src={att.url}
                    alt={att.name}
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )
              }
              if (kind === 'video') {
                return (
                  <Box
                    component="video"
                    src={att.url}
                    muted
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      bgcolor: '#000',
                    }}
                  />
                )
              }
              if (kind === 'pdf') {
                return (
                  <Box
                    component="iframe"
                    title={att.name || 'PDF'}
                    src={att.url}
                    sx={{
                      border: 0,
                      width: '140%',
                      height: '140%',
                      transform: 'scale(0.72)',
                      transformOrigin: 'top left',
                      pointerEvents: 'none',
                      bgcolor: '#fff',
                    }}
                  />
                )
              }
              if (kind === 'audio') {
                return (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      px: 1,
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 28, color: WA.meta }} />
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        color: WA.text,
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                      }}
                    >
                      {att.name}
                    </Box>
                  </Box>
                )
              }
              return (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    px: 1,
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 28, color: WA.meta }} />
                  <Box
                    component="span"
                    sx={{
                      fontSize: 10,
                      color: WA.text,
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    {att.name}
                  </Box>
                </Box>
              )
            })()}
            {att.uploading ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.45)',
                }}
              >
                <CircularProgress size={18} sx={{ color: WA.send }} />
              </Box>
            ) : null}
            <IconButton
              size="small"
              onClick={() => removeAttachmentAt(index)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                width: 22,
                height: 22,
                zIndex: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    ) : null
    if (welcomeLayout) {
      return (
        <Box sx={{ width: '100%', minWidth: 0, bgcolor: '#fff' }}>
          {replyTo ? (
            <Box
              sx={{
                mx: 2,
                mt: 1,
                mb: 0,
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                border: `1px solid ${WA.border}`,
                borderLeft: `3px solid ${WA.header}`,
                bgcolor: '#F4F6F8',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: WA.header, display: 'block' }}>
                  Replying to {replyTo.sender}
                </Typography>
                <Typography variant="caption" noWrap sx={{ color: WA.meta, display: 'block' }}>
                  {replyTo.preview}
                </Typography>
              </Box>
              <IconButton size="small" aria-label="Cancel reply" onClick={() => onClearReply?.()} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 16, color: WA.meta }} />
              </IconButton>
            </Box>
          ) : null}
          {attachmentPreviewBar}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            px: 2,
            py: 1.25,
            bgcolor: '#fff',
            borderTop: hasAttachments || replyTo ? 'none' : '1px solid #E7E7E7',
            gap: 0.5,
            width: '100%',
            minWidth: 0,
          }}
        >
          {renderWhatsappQuill(captionPlaceholder)}
          {(isMessageValid(message) || hasAttachments) && (
            <IconButton
              disabled={isUploadingAttachments || (!isMessageValid(message) && !hasAttachments)}
              onClick={handleSend}
              sx={{
                bgcolor: WA.send,
                color: '#fff',
                width: 36,
                height: 36,
                flexShrink: 0,
                mb: 0.5,
                '&:hover': { bgcolor: '#015A8E' },
                '&:disabled': { bgcolor: '#ccc', color: '#fff' },
              }}
            >
              {progress ? (
                <CircularProgress size={16} sx={{ color: '#fff' }} />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          )}
          <input
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Box>
        </Box>
      )
    }

    return (
      <Box sx={{ width: '100%', minWidth: 0, bgcolor: WA.inputBar }}>
        {replyTo ? (
          <Box
            sx={{
              mx: 1,
              mt: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.5,
              border: `1px solid ${WA.border}`,
              borderLeft: `3px solid ${WA.header}`,
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: WA.header, display: 'block' }}>
                Replying to {replyTo.sender}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: WA.meta, display: 'block' }}>
                {replyTo.preview}
              </Typography>
            </Box>
            <IconButton size="small" aria-label="Cancel reply" onClick={() => onClearReply?.()} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 16, color: WA.meta }} />
            </IconButton>
          </Box>
        ) : null}
        {attachmentPreviewBar}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          px: 1,
          py: 0.75,
          bgcolor: WA.inputBar,
          borderTop: hasAttachments || replyTo ? 'none' : '1px solid #DFE3E8',
          gap: 0.5,
          width: '100%',
          minWidth: 0,
        }}
      >
        {renderWhatsappQuill(captionPlaceholder)}
        <IconButton
          disabled={isUploadingAttachments || (!isMessageValid(message) && !hasAttachments)}
          onClick={handleSend}
          sx={{
            bgcolor: WA.send,
            color: '#fff',
            width: 40,
            height: 40,
            flexShrink: 0,
            mb: 0.25,
            boxShadow: '0 6px 14px rgba(2, 125, 194, 0.28)',
            '&:hover': { bgcolor: '#015A8E' },
            '&:disabled': { bgcolor: '#ccc', color: '#fff', boxShadow: 'none' },
          }}
        >
          {progress ? (
            <CircularProgress size={16} sx={{ color: '#fff' }} />
          ) : (
            <SendIcon fontSize="small" />
          )}
        </IconButton>
        <input
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Box>
      </Box>
    )
  }

  return (
    <RootStyle {...other}>
      <ReactQuill
        ref={quillRef}
        value={message}
        onChange={(val) => {
          console.log('valvalvalval 1', val)
          setMessage(val)
          const cleaned = val.replace(/<(.|\n)*?>/g, '').trim()
          if (cleaned.length > 0) {
            handleTyping()
          }
        }}
        modules={quillModules}
        placeholder="Type a message..."
        style={{ flexGrow: 1 }}
      />
      <Divider orientation="vertical" flexItem />
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ mx: 1 }}
        alignItems="center"
        justifyContent="center"
      >
        <EmojiPickerChatSignWise
          disabled={disabled}
          value={message}
          setValue={setMessage}
          onSend={handleSendStickers}
        />
        {/* message.replace(/<(.|\n)*?>/g, '').trim() */}
        <IconButton
          color="primary"
          disabled={isUploadingAttachments || (!isMessageValid(message) && !hasAttachments)}
          onClick={handleSend}
          sx={{
            backgroundColor: theme.palette.mode === 'light' 
              ? 'rgba(2, 125, 194, 0.1)' 
              : 'rgba(2, 125, 194, 0.2)',
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'light' 
                ? 'rgba(2, 125, 194, 0.15)' 
                : 'rgba(2, 125, 194, 0.3)',
              transform: 'scale(1.05)',
            },
            '&:disabled': {
              backgroundColor: theme.palette.mode === 'light' 
                ? 'rgba(0, 0, 0, 0.05)' 
                : 'rgba(255, 255, 255, 0.05)',
              color: theme.palette.text.disabled,
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {progress ? (
            <CircularProgress size={15} color="primary" />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Stack>
      <input
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </RootStyle>
  )
}
